<template>
  <page-wrapper :title="title" :has-pad="true">
    <div class="wrapper">
      <service-editor
        v-model="form"
        :icon-url="iconUrl"
        @removeDuration="handleRemoveDuration"
        @addDuration="handleAddDuration"
        @setEmptyServiceOptionCosts="handleEmptyOptionCost"
        @isCheckCombineOption="handleCheckCombineOption"
      ></service-editor>
      <div class="text-end">
        <sc-button variant="primary" @click="handleClickSaveService">บันทึก</sc-button>
      </div>
      <service-confirm-material-code
        v-if="isShowConfirmMaterialCode"
        v-model="form"
        @confirmMaterialCode="handleConfirmMaterialCode"
      ></service-confirm-material-code>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import { baanRueJaiClient } from "@/dependencies";
import { uploadPublicImage } from "@/god";
import { debounce } from "@/lib/util";
import { convertStangToBathAndRoundTwoDecimal } from "@/lib/formatter";
import { serviceType, sapServiceRecognizeRevenueType } from "@/lib/mapper";
import ServiceConfirmMaterialCode from "../components/ServiceConfirmMaterialCode";
import ServiceEditor from "../components/ServiceEditor";
import serviceFormMixin from "../mixins/serviceFormMixin";
import {
  formatFormServiceProductsForApi,
  getCreateServiceFormValidations,
  getServiceMaterialFieldsForApi,
  setupNewServiceOptions,
  setupNewServiceOptionCosts,
  formatDurationsForApi,
} from "../utils/serviceForm";

export default {
  components: {
    PageWrapper,
    ScButton,
    ServiceEditor,
    "service-confirm-material-code": ServiceConfirmMaterialCode
  },
  mixins: [serviceFormMixin],
  data() {
    const title = "เพิ่มบริการ";
    const form = {
      name: "",
      shortDescription: "",
      minimumAdvanceDuration: 5,
      quota: 1,
      cost: 0,
      provider: null,
      externalLink: null,
      description: "",
      termsAndConditions: "",
      iconFile: null,
      serviceOptions: null,
      appointDurationSlots: [],
      appointDateConstraints: [],
      serviceOptionCosts: null,
      hasOptionSwitch: false,
      isCanNotEditService: false,
      hasEditOptionCost: true,
      materialCode: null,
      materialQuantity: null,
      sapServiceType: "NORMAL",
      codeDescriptionUnit: null,
      unitName: null,
      sapServiceRecognizeRevenue: null,
      isVatRegistered: null,
      serviceType: serviceType.SERVICE,
      hasServiceProduct: false,
      serviceProductId: null,
      serviceProductName: "",
      serviceProductCode: "",
      serviceProductQuantity: null,
      serviceProductCost: null,
      serviceProducts: []
    };

    const iconUrl = null;
    const currentQuota = form.quota;

    return {
      title,
      form,
      iconUrl,
      currentQuota,
      isCheckCombineOption: false,
      isShowConfirmMaterialCode: false
    };
  },
  watch: {
    form(form, oldForm) {
      if (this.form.iconFile === oldForm.iconFile) {
        return;
      }

      this.iconUrl = URL.createObjectURL(this.form.iconFile);
    },
    "form.quota": debounce(function onQuotaChange(newQuota) {
      if (
        newQuota > this.currentQuota &&
        newQuota !== this.form.appointDurationSlots.length
      ) {
        this.handleAddAppointDurationSlot(newQuota, this.currentQuota);
        this.handleAddDateConstraints(newQuota, this.currentQuota);
      } else if (newQuota < this.currentQuota) {
        this.handleRemoveAppointDurationSlot(newQuota, this.currentQuota);
        this.handleRemoveDateConstraints(newQuota, this.currentQuota);
      }
      this.currentQuota = newQuota;
    }, 0.5)
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      const appointDurationSlot = {
        slotNumber: this.form.quota,
        durations: [
          {
            startAt: "",
            endAt: ""
          },
        ],
        defaultDurations: [
          {
            startAt: "09:00",
            endAt: "12:00"
          },
          {
            startAt: "13:00",
            endAt: "16:00"
          },
        ],
        isDefaultDuration: true
      };

      const appointDateConstraint = {
        slotNumber: this.form.quota,
        constraints: 0,
        reference_date_type: "PREVIOUS_APPOINTMENT_DATE",
        type: "APPOINTMENT_GAP"
      };

      this.form.appointDurationSlots.push(appointDurationSlot);
      this.form.appointDateConstraints.push(appointDateConstraint);
    },
    getValidations() {
      return getCreateServiceFormValidations({
        form: this.form,
        hasEmptyDuration: () => this.hasEmptyDuration(),
        hasEmptyAppointmentCost: () =>
          this.hasEmptyAppointmentCost(this.form.appointDurationSlots),
        hasEmptyCombineOption: () => this.hasEmptyCombineOption(),
        hasEmptyOptionCost: () => this.hasEmptyMainCombineOption(),
        hasEmptyExtraOptionCost: () => this.hasEmptyExtraCombineOption(),
        isCheckCombineOption: this.isCheckCombineOption,
        shouldValidateOptionCosts: () => this.form.serviceOptionCosts,
        minimumAdvanceDurationMessage:
          "ไม่สามารถเพิ่มบริการได้เนื่องจากจำนวนวันนัดล่วงหน้าน้อยกว่า 1 วัน",
      });
    },
    async handleClickSaveService() {
      const validations = this.getValidations();
      const hasFailedValidation = validations.some((v) => {
        if (v.check()) {
          this.$message.error(v.message);
          return true;
        }
        return false;
      });
      if (hasFailedValidation) {
        return;
      }

      if (this.form.quota === 0) {
        this.form.sapServiceRecognizeRevenue = sapServiceRecognizeRevenueType.APPOINTMENT_REVENUE;
      }

      this.isShowConfirmMaterialCode = true;
    },
    async handleClickSave() {
      try {
        await this.createService();
        this.$router.push({ name: "ServiceListPage" });
      } catch (err) {
        this.$message.error("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
        console.error(err);
      }
    },
    async createService() {
      const {
        name,
        shortDescription,
        minimumAdvanceDuration,
        quota,
        cost,
        provider,
        externalLink,
        description,
        termsAndConditions,
        iconFile,
        serviceOptions,
        appointDurationSlots,
        serviceOptionCosts,
        hasServiceProduct,
        materialCode,
        materialQuantity,
        sapServiceType,
        sapServiceRecognizeRevenue,
        serviceType: formServiceType,
        serviceProducts: selectedServiceProducts
      } = this.form;

      const newServiceOptions = serviceOptions
        ? setupNewServiceOptions(serviceOptions)
        : null;

      const newServiceOptionCosts = serviceOptionCosts
        ? setupNewServiceOptionCosts(serviceOptionCosts)
        : null;

      const options = {
        service_options: newServiceOptions,
        service_option_costs: newServiceOptionCosts
      };
      const serviceProductsPayload = formatFormServiceProductsForApi({
        serviceProducts: selectedServiceProducts,
        serviceOptions,
        serviceOptionCosts,
        hasServiceProduct,
        serviceType: formServiceType,
      });
      const serviceMaterialFields = getServiceMaterialFieldsForApi({
        materialCode,
        materialQuantity,
        serviceOptions,
      });

      const icon = await uploadPublicImage(iconFile);
      const appointDurationSlotsForCreate = this.setupAppointDurationSlotsForCreate(
        appointDurationSlots
      );
      const providerId = provider.id;
      const externalLinkValue = externalLink;

      const params = {
        name,
        short_description: shortDescription,
        minimum_advance_duration: minimumAdvanceDuration,
        quota,
        cost: convertStangToBathAndRoundTwoDecimal(cost),
        provider_id: providerId,
        external_link: externalLinkValue,
        description,
        terms_and_conditions: termsAndConditions,
        icon,
        // TODO: remove empty list when appoint date constraint and appoint duration ready on production
        appoint_duration_slots: appointDurationSlotsForCreate,
        appoint_date_constraints: [],
        options,
        ...serviceMaterialFields,
        sap_service_type: sapServiceType,
        sap_service_recognize_revenue: sapServiceRecognizeRevenue,
        service_type: formServiceType
      };

      if (serviceProductsPayload.length > 0) {
        params.service_products = serviceProductsPayload;
      }

      const response = await baanRueJaiClient.admin.subscriptions.services.post(
        params
      );

      return response.data.result;
    },
    setupNewServiceOptions(serviceOptions) {
      const newServiceOptions = serviceOptions.map((option) => {
        const variants = option.variants.map((variant) => ({
            variant_id: variant.variant_id,
            is_not_sale: variant.is_not_sale,
            is_out_of_stock: variant.is_out_of_stock,
            title: variant.title,
            material_code: variant.material_code,
            material_quantity: variant.material_quantity
          }));

        return {
          id: option.id,
          extra_option: option.extra_option,
          title: option.title,
          variants
        };
      });

      return newServiceOptions;
    },
    setupNewServiceOptionCosts(serviceOptionCosts) {
      const newServiceOptionCosts = serviceOptionCosts.map((optionCost) => {
        const combinationCosts = optionCost.combination_costs.map((combinationCost) => {
          const costs = combinationCost.appointment_costs.map((appointCost) => ({
              appoint_no: appointCost.appoint_no,
              cost: appointCost.cost !== null
                ? parseFloat(appointCost.cost)
                : null
            }))

          return {
            id: combinationCost.id,
            is_not_sale: combinationCost.is_not_sale,
            is_out_of_stock: combinationCost.is_out_of_stock,
            title: combinationCost.title,
            appointment_costs: costs,
            material_code: combinationCost.material_code,
            material_quantity: combinationCost.material_quantity
          }
        })

        return {
          id: optionCost.id,
          is_product: optionCost.is_product,
          title: optionCost.title,
          combination_costs: combinationCosts
        }
      })

      return newServiceOptionCosts;
    },
    setupAppointDurationSlotsForCreate(appointDurationSlots) {
      const newAppointDurationSlots = appointDurationSlots.map((slot) => {
        const slotNumberStr = slot.slotNumber.toString();
        const durations = slot.isDefaultDuration
          ? formatDurationsForApi(slot.defaultDurations)
          : formatDurationsForApi(slot.durations);
        const appointCost = convertStangToBathAndRoundTwoDecimal(slot.appointCost);

        return {
          slot_number: slotNumberStr,
          durations,
          appoint_cost: appointCost
        };
      });

      return newAppointDurationSlots;
    },
    handleEmptyOptionCost() {
      this.form.serviceOptionCosts = null;
    },
    handleConfirmMaterialCode(value) {
      if (value) {
        this.handleClickSave();
      }
      this.isShowConfirmMaterialCode = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.wrapper {
  padding: $space 0;
}

.editor {
  margin-bottom: $space;
}
</style>
